import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Log, PriceRateAdditionPricingType } from "@ryde/shared";

import { ColorIconButton } from "../../../controls";
import { NumberTemplate } from "../number.template";
import {
  emptyPriceLevel,
  IPriceLevelItem,
  IPriceLevelItemItemForm,
  priceLevelFormFieldNames,
} from "./price-level.models";
import { usePriceLevelItemResolver } from "./use-price-level-item-validation";

export const PriceLevelItemEdit: FC<IPriceLevelItemItemForm> = ({
  pricingType,
  index,
  fromValues,
  value,
  onCancelOuter,
  setValueOuter,
  fieldName,
}) => {
  const { t } = useTranslation();
  const { priceLevelFormValidationSchema } = usePriceLevelItemResolver({
    fromValues,
    itemIndex: index,
  });

  const methods = useForm<Partial<IPriceLevelItem>>({
    defaultValues: value || emptyPriceLevel,
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(priceLevelFormValidationSchema),
  });

  const {
    trigger,
    getValues,
    watch,
    formState: { isValid, touchedFields, dirtyFields },
  } = methods;

  const onSaveClick = async (): Promise<void> => {
    const validated = await trigger();

    if (!validated) {
      return;
    }

    const valueSnapshot = getValues();

    Log.debug(
      `Set value for availability item target field:  ${fieldName}`,
      valueSnapshot,
    );
    setValueOuter?.(fieldName, valueSnapshot, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCancelOuter?.();
  };

  const fieldFromValue = watch("from");
  const fieldPriceValue = watch("price");

  useEffect(() => {
    for (const dependantFieldName of priceLevelFormFieldNames) {
      Log.debug(
        "Trigger dependant controls validation for ",
        dependantFieldName,
        fieldFromValue,
        fieldPriceValue,
      );

      if (
        touchedFields[dependantFieldName] ||
        dirtyFields[dependantFieldName]
      ) {
        trigger(dependantFieldName);
      }
    }
  }, [trigger, fieldFromValue, fieldPriceValue, touchedFields, dirtyFields]);

  return (
    <FormProvider {...methods}>
      <div className="price-level-item">
        <div className="price-level-item__editors">
          <NumberTemplate
            labelTranslated={
              pricingType === PriceRateAdditionPricingType.Time
                ? t("priceRate.formLabel.startFromTime")
                : t("priceRate.formLabel.startFromDistance")
            }
            name="from"
          />

          <NumberTemplate
            labelTranslated={
              pricingType === PriceRateAdditionPricingType.Time
                ? t("priceRate.formLabel.pricePerMinute")
                : t("priceRate.formLabel.pricePerKilometer")
            }
            name="price"
          />
        </div>
        <div className="availability-item__buttons">
          <ColorIconButton
            disabled={!isValid}
            iconType="CHECK"
            edge="end"
            aria-label="save"
            onClick={onSaveClick}
          />

          <ColorIconButton
            iconType="CANCEL"
            edge="end"
            aria-label="cancel"
            onClick={() => onCancelOuter?.()}
          />
        </div>
      </div>
    </FormProvider>
  );
};
